/**
 * Whether what you are rendering is already inside a named section.
 *
 * ## The defect this exists to remove
 *
 * `AsyncRegion` renders the four designed states over a query, and three of them are an
 * `EmptyState`. `EmptyState` has two placements (ADR 9006): `standalone` centres itself
 * under a condensed uppercase heading and is the designed surface for a whole screen that
 * has nothing to show; `inline` is left-aligned, quieter and headed with the subsection
 * face, for a slot inside a panel that has already named itself.
 *
 * `AsyncRegion` hard-coded `inline`. That was right for its common mount — inside a
 * `Panel` — and it was measured on a real screen: standalone centring inside a panel put
 * two condensed uppercase headings one under the other. But a screen-level `AsyncRegion`
 * WRAPS its panels rather than sitting inside one, and there it rendered a bare heading
 * and a paragraph with no container at all. `/exit/position` showed two of them, stacked,
 * identical, floating on the page background. The same shape had already been recorded
 * once as a defect on the cockpit — "one panel in four had no container and no heading".
 *
 * ## Why a context and not a prop
 *
 * A `placement` prop would have worked and would have been wrong: it asks every one of the
 * mounts to remember a fact about its own surroundings, which is exactly the kind of fact a
 * copy-paste loses. The component was not missing an option; it was **guessing its own
 * context**. A container that names itself says so once, here, and every designed state
 * inside it — now and whenever another is added — reads the answer rather than re-deciding
 * it.
 *
 * The default is `false`: a component that nothing has enclosed is on the page, and the
 * page's designed state is the standalone one.
 *
 * ## Why it lives in `primitives/`, and is read by `EmptyState` itself
 *
 * It began in `src/planning/`, read by `AsyncRegion`. That covered the empty states a query
 * draws and missed every one a screen draws directly: audit v4 G-09 found "NAME A CYCLE RUN"
 * — the in-season run gate, an `EmptyState` inside a `Panel` — still centred in the display
 * face under the panel's own display heading, beside an inline "Nothing needs a decision".
 * The fact belongs to the component that has two placements, so `EmptyState` reads it, and
 * `planning/` depends on `primitives/` rather than the other way round.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

const EnclosedContext = createContext(false);

/**
 * Marks its children as sitting inside a section that carries its own heading and
 * container. `Panel` mounts this itself, so a screen built out of panels needs nothing;
 * it is exported for a container that names itself without being a `Panel`.
 */
export function Enclosure({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <EnclosedContext.Provider value>{children}</EnclosedContext.Provider>;
}

/** True when a named container has already introduced what you are about to render. */
export function useEnclosed(): boolean {
  return useContext(EnclosedContext);
}
